import axios from 'axios';
import React, { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal';
import toast from 'react-hot-toast';
import * as Yup from "yup";
import useCustomValidation from '../../../hooks/useCustomValidation';
import useLogout from '../../../hooks/useLogout';
import ValidationError from '../../ValidationError';
import RegexInput from '../../RegexInput';

function CreateUPIModal({ createData, show, handleClose }) {
    const logoutUser = useLogout();

    const [isLoading, setIsLoading] = useState(false);
    const [regexStatus, setRegexStatus] = useState(null);
    const [staticQrCode, setStaticQrCode] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

    const schema = {
        name: Yup.string().required("UPI Name is required").trim(),
        vpa: Yup.string().required("UPI Id is required").trim(),
        isRegex: Yup.bool().required("Is Regex Enabled is required"),
        staticQrCode: Yup.bool().required("Qr Code is required"),
        qrCodeImage: Yup
            .mixed()
            .when('staticQrCode', {
                is: true, // Condition: if 'staticQrCode' is true
                then: (schema) =>
                    schema
                        .required('Qr Code Image is required.')
                        .test(
                            'fileSize',
                            'File is too large (max 5MB)',
                            (value) => value && value[0] && value[0].size <= 5 * 1024 * 1024 // 5MB
                        )
                        .test(
                            'fileType',
                            'Invalid file type (only .jpg, .png, .webp)',
                            (value) =>
                                value &&
                                value[0] &&
                                ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(
                                    value[0].type
                                )
                        ),
                otherwise: (schema) => schema.notRequired(), // Otherwise, it's not required
            }),
        regexPattern: Yup.string().trim(),
    };
    const initial = {
        name: null,
        vpa: null,
        isRegex: false,
        staticQrCode: false,
        qrCodeImage: null,
        regexPattern: ""
    };
    const { register, errors, handleSubmit, reset, getValues, setValue, watch } =
        useCustomValidation({ schema, initial });

    const imageFile = watch('qrCodeImage');

    useEffect(() => {
        if (imageFile && imageFile.length > 0) {
            const file = imageFile[0]; // Access the File object from the FileList
            const url = URL.createObjectURL(file);
            setImagePreviewUrl(url);

            // Clean up the object URL when the component unmounts or the file changes
            return () => {
                URL.revokeObjectURL(url);
                setImagePreviewUrl(null); // Clear the preview when no file is selected or file changes
            };
        } else {
            // If no file is selected, clear the preview
            setImagePreviewUrl(null);
        }
    }, [imageFile]);

    const toggleRegexStatus = () => {
        const status = getValues("isRegex")
        setValue("isRegex", !status)
        setRegexStatus(!status)
    }
    const toggleStaticQrCode = () => {
        const status = getValues("staticQrCode")
        setValue("staticQrCode", !status)
        setStaticQrCode(!status)
    }

    const createNewUpiSubmit = async (data) => {

        // console.log(data)
        setIsLoading(true)

        const formData = new FormData();
        formData.append("qrCodeImage", data?.qrCodeImage ? data.qrCodeImage[0] : null);
        formData.append("merchant", createData.merchant);
        formData.append("isRegex", data.isRegex);
        formData.append("name", data.name);
        formData.append("regexPattern", data.regexPattern);
        formData.append("staticQrCode", data.staticQrCode);
        formData.append("vpa", data.vpa);

        await axios
            .post(`${process.env.REACT_APP_API_URL}/merchant/upi/create`,
                formData)
            .then((response) => {
                const { data } = response;
                if (response.status == 201) {
                    toast.success(data.msg);
                    reset()
                    handleClose("success")
                } else {
                    toast.error(data.msg);
                }
            })
            .catch((error) => {
                const { response } = error
                if (response) {
                    const { data } = response
                    if (response?.status == 401) {
                        toast.error("Please Login!");
                        logoutUser();
                    } else if (response?.status == 400) {
                        toast.error(data.msg);
                    } else {
                        toast.error(data.msg || "Request failed!");
                    }
                }
            })
            .finally((e) => {
                setIsLoading(false)
            });

    }

    const handleOnHide = () => {
        reset()
        handleClose("manual")
    }

    return (
        <Modal show={show} onHide={handleOnHide}>
            <Modal.Header closeButton>
                <Modal.Title>Create UPI</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit(createNewUpiSubmit)}>
                    <div className="mb-3">
                        <label htmlFor='c-upi-name' className="form-label">UPI Name</label>
                        <input type="text" {...register("name")} className="form-control"
                            id="c-upi-name" />
                        <ValidationError name="name" errors={errors} />
                    </div>
                    <div className="mb-3">
                        <label htmlFor='c-upi-vpa' className="form-label">UPI Id</label>
                        <input {...register("vpa")} type="text" className="form-control"
                            id="c-upi-vpa" />
                        <ValidationError name="vpa" errors={errors} />
                    </div>
                    <div className="mb-3">
                        <label htmlFor='u-upi-active' className="form-label">Qr Code</label>
                        <div className="form-check form-switch">
                            <input {...register("staticQrCode")} value={staticQrCode} onChange={toggleStaticQrCode} className="form-check-input" type="checkbox"
                                id="u-upi-active" checked={staticQrCode} />
                            <label className="form-check-label" htmlFor="u-upi-active">{staticQrCode ? "Upload Qr Code" : "Auto generat using UPI Id"}</label>
                        </div>
                        <ValidationError name="staticQrCode" errors={errors} />
                    </div>
                    {staticQrCode && (
                        <div className="mb-3">
                            <div className="container p-3 border rounded">
                                <h6 className="form-label mb-3">Upload Qr Code</h6>
                                <input type="file" {...register("qrCodeImage")}
                                    name="qrCodeImage"
                                    accept="image/jpeg, image/jpg, image/png, image/webp"
                                    multiple={false}
                                    className="form-control" id="upload-qrCodeImage" />
                                {/* Image preview section */}
                                {imagePreviewUrl && (
                                    <div className='mt-3'>
                                        <h6>Preview</h6>
                                        <div className='d-flex flex-column align-items-center'>
                                            <img
                                                src={imagePreviewUrl}
                                                alt="Image Preview"
                                                style={{ maxWidth: '200px', maxHeight: '200px' }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <ValidationError name="qrCodeImage" errors={errors} />
                            </div>
                        </div>
                    )}


                    <div className="mb-3">
                        <label htmlFor='u-upi-active' className="form-label">Is Regex</label>
                        <div className="form-check form-switch">
                            <input {...register("isRegex")} value={regexStatus} onChange={toggleRegexStatus} className="form-check-input" type="checkbox"
                                id="u-upi-active" checked={regexStatus} />
                            <label className="form-check-label" htmlFor="u-upi-active">{regexStatus ? "Enabled" : "Disabled"}</label>
                        </div>
                        <ValidationError name="isRegex" errors={errors} />
                    </div>

                    {regexStatus && (
                        <div className="mb-3">
                            {/* <label htmlFor='c-upi-regexPattern' className="form-label">Regex Pattern</label>
                            <input {...register("regexPattern")} type="text" className="form-control"
                                id="c-upi-regexPattern" /> */}
                            <RegexInput onChange={(reg) => { setValue("regexPattern", reg); }} value={getValues("regexPattern")} />
                            <ValidationError name="regexPattern" errors={errors} />
                        </div>
                    )}

                    <button type="submit" className="btn btn-primary"
                        disabled={isLoading}>
                        {isLoading && (
                            <div className="spinner-border"></div>
                        )} Create
                    </button>
                </form>
            </Modal.Body>
        </Modal>
    )
}

export default CreateUPIModal